/**
 * 字符串模板替换工具函数
 * 使用单次遍历和正则表达式缓存来提高性能
 */

type ReplacementMap = Record<string, string>

// 缓存正则表达式以提高性能
const regexCache = new Map<string, RegExp>()

/**
 * 转义正则表达式特殊字符
 * @param str 需要转义的字符串
 * @returns 转义后的字符串
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * 获取或创建正则表达式（带缓存）
 * @param beginSeg 占位符开始符号，如 "{"
 * @param endSeg 占位符结束符号，如 "}"
 * @returns 编译后的正则表达式
 */
function getRegex(beginSeg: string, endSeg: string): RegExp {
  const cacheKey = `${beginSeg}|${endSeg}`

  if (regexCache.has(cacheKey)) {
    return regexCache.get(cacheKey)!
  }

  // 转义特殊字符，构建正则表达式
  // 匹配 beginSeg + key + endSeg，key 可以是字母、数字、下划线、连字符
  const escapedBegin = escapeRegExp(beginSeg)
  const escapedEnd = escapeRegExp(endSeg)
  const regex = new RegExp(`${escapedBegin}([a-zA-Z0-9_-]+)${escapedEnd}`, 'g')

  regexCache.set(cacheKey, regex)
  return regex
}

/**
 * 字符串模板替换函数
 * @param template 模板字符串，如 "Hello {name}, you have {count} messages"
 * @param replacements 替换对象，如 { name: "John", count: "5" }
 * @param beginSeg 占位符开始符号，默认为 "{"
 * @param endSeg 占位符结束符号，默认为 "}"
 * @returns 替换后的字符串
 *
 * @example
 * ```ts
 * replaceTemplate("Hello {name}!", { name: "World" })
 * // => "Hello World!"
 *
 * replaceTemplate("Price: {{amount}}", { amount: "$100" }, "{{", "}}")
 * // => "Price: $100"
 *
 * replaceTemplate("You have {count} items", { count: "5", unused: "test" })
 * // => "You have 5 items" (unused 键会被忽略)
 *
 * replaceTemplate("Hello ${name}!", { name: "World" }, "${", "}")
 * // => "Hello World!"
 * ```
 */
export function replaceTemplate(
  template: string,
  replacements: ReplacementMap,
  beginSeg: string = '{',
  endSeg: string = '}'
): string {
  if (!template) return template
  if (!replacements || Object.keys(replacements).length === 0) return template

  const regex = getRegex(beginSeg, endSeg)

  // 使用 replace 方法进行替换，性能优于手动遍历
  return template.replace(regex, (match, key) => {
    // 如果 key 存在于 replacements 中，则返回对应的值
    // 否则保持原样（返回整个匹配内容）
    return replacements[key] !== undefined ? replacements[key] : match
  })
}

/**
 * 批量字符串模板替换函数（用于一次性替换多个模板）
 * @param templates 模板字符串数组
 * @param replacements 替换对象
 * @param beginSeg 占位符开始符号，默认为 "{"
 * @param endSeg 占位符结束符号，默认为 "}"
 * @returns 替换后的字符串数组
 *
 * @example
 * ```ts
 * replaceTemplates(
 *   ["Hello {name}!", "You have {count} items"],
 *   { name: "John", count: "5" }
 * )
 * // => ["Hello John!", "You have 5 items"]
 *
 * replaceTemplates(
 *   ["Hello {{name}}!", "Price: {{amount}}"],
 *   { name: "John", amount: "$100" },
 *   "{{",
 *   "}}"
 * )
 * // => ["Hello John!", "Price: $100"]
 * ```
 */
export function replaceTemplates(
  templates: string[],
  replacements: ReplacementMap,
  beginSeg: string = '{',
  endSeg: string = '}'
): string[] {
  return templates.map(template => replaceTemplate(template, replacements, beginSeg, endSeg))
}

/**
 * 从模板字符串中提取所有占位符键名
 * @param template 模板字符串
 * @param beginSeg 占位符开始符号，默认为 "{"
 * @param endSeg 占位符结束符号，默认为 "}"
 * @returns 占位符键名数组（去重）
 *
 * @example
 * ```ts
 * extractPlaceholders("Hello {name}, you have {count} new {count} messages")
 * // => ["name", "count"]
 *
 * extractPlaceholders("Hello {{name}}, price is {{amount}}", "{{", "}}")
 * // => ["name", "amount"]
 * ```
 */
export function extractPlaceholders(template: string, beginSeg: string = '{', endSeg: string = '}'): string[] {
  if (!template) return []

  const regex = getRegex(beginSeg, endSeg)
  const keys = new Set<string>()
  let match: RegExpExecArray | null

  // 使用 exec 循环替代 matchAll 以保证兼容性
  while ((match = regex.exec(template)) !== null) {
    keys.add(match[1])
  }

  return Array.from(keys)
}
